import {createRouter, createWebHashHistory} from 'vue-router';

import {useAuthStore} from "@/stores/auth.store";
import {useHomeStore} from "@/stores/home.store";
import {EPermissao} from "@/core/type/permissao.type";
import AppLayout from "@/layout/AppLayout.vue";
import {useAppHelper} from "@/core/helpers/app.helper";

import {apuracaoNotaRoutes} from "@/modules/apuracaoNota";
import {artigoRoutes} from "@/modules/artigo";
import {finalidadeRoutes} from "@/modules/finalidade";
import {atividadesRoutes} from "@/modules/atividades";
import {boletoRoutes} from "@/modules/boleto";
import {colaboradorRoutes} from "@/modules/colaborador";
import {cteRoutes} from "@/modules/cte";
import {extratoRoutes} from "@/modules/extrato";
import {fluxoCaixaRoutes} from "@/modules/fluxoCaixa";
import {lancamentoRoutes} from "@/modules/lancamento";
import {lancamentoRecorrenciaRoutes} from "@/modules/lancamentoRecorrencia";
import {listaArranqueRoutes} from "@/modules/listaArranque";
import {manutencaoPreventivaRoutes} from "@/modules/manutencaoPreventiva";
import {nfseRoutes} from "@/modules/nfse";
import {ordemFabricacaoRoutes} from "@/modules/ordemFabricacao";
import {pedidoCompraRoutes} from "@/modules/pedidoCompra";
import {relatorioRoutes} from "@/modules/relatorio";
import {requisicaoCompraRoutes} from "@/modules/requisicaoCompra";
import {transferenciaRoutes} from "@/modules/transferencia";
import {usuarioRoutes, usuarioRoutesPublicas} from "@/modules/usuario";

const {checkForUpdate} = useAppHelper();

/*
 * O router só AGREGA: cada módulo declara as próprias rotas em `modules/{modulo}/routes.ts`
 * e reexporta pelo barrel. Assim o módulo fica autocontido — apagar a pasta remove o
 * módulo inteiro, rotas incluídas — e este arquivo para de crescer a cada tela nova.
 * Aqui ficam só as rotas transversais (home, permissão negada, tela de teste).
 */
const router = createRouter({
    history: createWebHashHistory(),
    routes: [
        //Rotas públicas: ficam fora do AppLayout (shell autenticado)
        ...usuarioRoutesPublicas,
        {
            path: "/teste",
            meta: {authRequired: true},
            component: () => import('@/views/pages/teste/TesteComponent.vue')
        },
        {
            path: '/',
            component: AppLayout,
            children: [
                {
                    path: '/',
                    name: 'home',
                    meta: {authRequired: true},
                    component: () => import('@/views/pages/home/PageHome.vue')
                },
                {
                    path: '/permissao_negada',
                    name: 'permissao_negada',
                    component: () => import("@/layout/layoutPages/PagePermissaoNegada.vue")
                },

                ...usuarioRoutes,
                ...listaArranqueRoutes,
                ...extratoRoutes,
                ...relatorioRoutes,
                ...requisicaoCompraRoutes,
                ...pedidoCompraRoutes,
                ...transferenciaRoutes,
                ...apuracaoNotaRoutes,
                ...atividadesRoutes,
                ...lancamentoRoutes,
                ...lancamentoRecorrenciaRoutes,
                ...fluxoCaixaRoutes,
                ...manutencaoPreventivaRoutes,
                ...artigoRoutes,
                ...finalidadeRoutes,
                ...ordemFabricacaoRoutes,
                ...boletoRoutes,
                ...cteRoutes,
                ...colaboradorRoutes,
                ...nfseRoutes,
            ]
        },
    ]
});

router.beforeEach(async (to, from, next) => {

    const homeStore = useHomeStore();
    homeStore.isShowPaceLoading = true;
    const authStore = useAuthStore();
    const authRequired = (!to.meta ? true : to.meta.authRequired);

    //Verifica se a rota precisa de autenticação
    if (!authRequired) {
        //Se não precisar, redireciona para a rota solicitada
        return next();

    } else {

        //A partir daqui, a rota precisa de autorização
        //Verifica se o usuário está logado através da store isLoggedIn e Permissoes

        if (!(authStore.isLoggedIn() && authStore.usuarioPermissoes)) {
            //Nao esta logado ou não tem permissao

            //Se não tiver logado direciona para o login
            if (!authStore.isLoggedIn()) {
                authStore.sair();
                next('/signin')
            } else {

                //Aciona o método que verifica se o usuário está ativo, por meio da sessão ou token válido
                if (!(await authStore.verificarUsuarioAtivo())) {
                    //Se não conseguiu verificar o usuario ou usuario não ativo, direciona para o login
                    authStore.sair();
                    next('/signin')
                }
            }

        }
        //fim da verificação do usuario logado

        //Verifica se a rota precisa de permissão
        if (to.meta.permissao) {
            //precisa
            //Verifica se o usuário possui a permissão solicitada pela rota
            if (authStore.temPermissao(to.meta.permissao as EPermissao)) {
                //tem = continua para rota solicitada
                return next();

            } else {
                //não tem = direciona para pagina de acesso negado
                return next('/permissao_negada');
            }
        } else {
            //não precisa = continua para rota solicitada
            return next();
        }

    }

});

router.afterEach(() => {

    const homeStore = useHomeStore();
    homeStore.isShowPaceLoading = false;
    checkForUpdate();
})


export default router;
